import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, DollarSign, TrendingUp, Database, Zap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const ADMIN_ROUTE = 'admin-mgmt-7f84b';

const APICostsPage = ({ user }) => {
  const navigate = useNavigate();
  const [apiCostsData, setApiCostsData] = useState(null);
  const [dateRange, setDateRange] = useState('30d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);

  const loadApiCosts = async (customRange, customStart, customEnd) => {
    try {
      setLoading(true);
      const params = {};
      
      if (customRange) {
        params.date_range = customRange;
      } else if (customStart && customEnd) {
        params.start_date = customStart;
        params.end_date = customEnd;
      } else {
        params.date_range = dateRange;
      }
      
      const response = await axios.get(`${API}/api/${ADMIN_ROUTE}/api-costs`, { 
        params,
        withCredentials: true 
      });
      setApiCostsData(response.data);
    } catch (error) {
      toast.error('Failed to load API cost data');
      console.error('API costs error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    loadApiCosts(range);
  };

  const handleCustomDateApply = () => {
    if (startDate && endDate) {
      loadApiCosts(null, startDate, endDate);
    }
  };

  // Check admin access and load initial data
  useEffect(() => {
    if (!user || (user.role !== 'admin' && !user.is_admin_designated)) {
      toast.error('Admin access required');
      navigate('/homepage');
      return;
    }
    loadApiCosts();
  }, []);

  // Group services by workflow
  const groupServicesByWorkflow = () => {
    if (!apiCostsData?.by_service) return {};

    const workflows = {
      'Development': ['BCTG', 'BVADM', 'BOCP', 'CCG', 'CCAT', 'BREC'],
      'Formatting': ['MMG', 'ABG', 'PDE'],
      'Marketing & Promotion': ['KSG', 'MMKG', 'SMMK'],
      'Design & Production': ['ERMT', 'PRMPB', 'AKSW']
    };

    const grouped = {};
    
    for (const [workflow, serviceCodes] of Object.entries(workflows)) {
      const services = apiCostsData.by_service.filter(s => 
        serviceCodes.includes(s.service)
      );
      
      if (services.length > 0) {
        const totalCost = services.reduce((sum, s) => sum + s.total_cost, 0);
        const totalCalls = services.reduce((sum, s) => sum + s.calls, 0);
        const totalTokens = services.reduce((sum, s) => sum + s.tokens, 0);
        
        grouped[workflow] = {
          services,
          totalCost,
          totalCalls,
          totalTokens
        };
      }
    }

    return grouped;
  };

  const getWorkflowColor = (workflow) => {
    const colors = {
      'Development': 'from-blue-500 to-blue-600',
      'Formatting': 'from-green-500 to-green-600',
      'Marketing & Promotion': 'from-amber-500 to-orange-600',
      'Design & Production': 'from-rose-500 to-pink-600'
    };
    return colors[workflow] || 'from-slate-500 to-slate-600';
  };

  const getWorkflowIcon = (workflow) => {
    const icons = {
      'Development': '📝',
      'Formatting': '🎨',
      'Marketing & Promotion': '📈',
      'Design & Production': '📕'
    };
    return icons[workflow] || '💼';
  };

  const getServiceName = (code) => {
    const names = {
      'BCTG': 'Book Concept & Title Generator',
      'BVADM': 'Book Viability & Development Model',
      'BOCP': 'Book Outline & Chapter Planner',
      'CCG': 'Character & Cast Generator',
      'CCAT': 'Competitor & Comparative Analysis',
      'BREC': 'Book Review & Endorsement Creator',
      'MMG': 'Manuscript Metadata Generator',
      'ABG': 'Author Biography Generator',
      'PDE': 'Publishing Dashboard Essentials',
      'KSG': 'Keynote Speech Generator',
      'MMKG': 'Manuscript Media Kit Generator',
      'SMMK': 'Social Media Marketing Kit',
      'ERMT': 'EPUB-Ready Manuscript Transformer',
      'PRMPB': 'Print Ready Merged PDF Builder',
      'AKSW': 'Amazon KDP Submission Wizard'
    };
    return names[code] || code;
  };

  const workflowGroups = groupServicesByWorkflow();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/homepage')}
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return to Admin
              </Button>
              <div>
                <h1 className="text-3xl font-bold" style={{ fontFamily: "'Times New Roman', serif" }}>
                  API Cost Analytics
                </h1>
                <p className="text-amber-100 mt-1">Track and analyze API tokenization costs across all services</p>
              </div>
            </div>
          </div>

          {/* Time Period Filters */}
          <div className="mt-6 flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant={dateRange === '7d' ? 'secondary' : 'outline'}
              onClick={() => handleDateRangeChange('7d')}
              className={dateRange === '7d' ? 'bg-white text-amber-700' : 'text-white border-white/50 hover:bg-white/20'}
            >
              7 Days
            </Button>
            <Button
              size="sm"
              variant={dateRange === '30d' ? 'secondary' : 'outline'}
              onClick={() => handleDateRangeChange('30d')}
              className={dateRange === '30d' ? 'bg-white text-amber-700' : 'text-white border-white/50 hover:bg-white/20'}
            >
              30 Days
            </Button>
            <Button
              size="sm"
              variant={dateRange === '90d' ? 'secondary' : 'outline'}
              onClick={() => handleDateRangeChange('90d')}
              className={dateRange === '90d' ? 'bg-white text-amber-700' : 'text-white border-white/50 hover:bg-white/20'}
            >
              90 Days
            </Button>
            <Button
              size="sm"
              variant={dateRange === 'custom' ? 'secondary' : 'outline'}
              onClick={() => setDateRange('custom')}
              className={dateRange === 'custom' ? 'bg-white text-amber-700' : 'text-white border-white/50 hover:bg-white/20'}
            >
              Custom Range
            </Button>
          </div>

          {dateRange === 'custom' && (
            <div className="mt-4 flex gap-3 items-end flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <Label htmlFor="startDate" className="text-white">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-white/20 border-white/30 text-white placeholder-white/60"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <Label htmlFor="endDate" className="text-white">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-white/20 border-white/30 text-white placeholder-white/60"
                />
              </div>
              <Button
                onClick={handleCustomDateApply}
                disabled={!startDate || !endDate}
                className="bg-white text-amber-700 hover:bg-white/90"
              >
                Apply
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
            <p className="text-slate-600 mt-4">Loading API cost data...</p>
          </div>
        ) : apiCostsData ? (
          <div className="space-y-8">
            {/* Overall Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">Total API Costs</p>
                      <p className="text-3xl font-bold text-amber-700 mt-1">
                        ${apiCostsData.summary.total_cost.toFixed(4)}
                      </p>
                    </div>
                    <DollarSign className="w-10 h-10 text-amber-500" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">Total API Calls</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1">
                        {apiCostsData.summary.total_api_calls.toLocaleString()}
                      </p>
                    </div>
                    <Zap className="w-10 h-10 text-blue-500" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">Total Tokens</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1">
                        {apiCostsData.summary.total_tokens_used.toLocaleString()}
                      </p>
                    </div>
                    <Database className="w-10 h-10 text-green-500" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">Avg Cost/Call</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1">
                        ${apiCostsData.summary.average_cost_per_call.toFixed(4)}
                      </p>
                    </div>
                    <TrendingUp className="w-10 h-10 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Costs by Workflow */}
            {Object.keys(workflowGroups).length > 0 ? (
              <>
                <h2 className="text-2xl font-bold text-slate-900 mt-8">Costs by Workflow</h2>
                
                <div className="space-y-6">
                  {Object.entries(workflowGroups).map(([workflow, data]) => (
                    <Card key={workflow} className="border-2">
                      <CardHeader className={`bg-gradient-to-r ${getWorkflowColor(workflow)} text-white`}>
                        <CardTitle className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{getWorkflowIcon(workflow)}</span>
                            <span>{workflow}</span>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold">${data.totalCost.toFixed(4)}</div>
                            <div className="text-sm opacity-90">
                              {data.totalCalls.toLocaleString()} calls • {data.totalTokens.toLocaleString()} tokens
                            </div>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-6">
                        <div className="space-y-3">
                          {data.services.map((service) => (
                            <div key={service.service} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 hover:shadow-md transition-shadow">
                              <div className="flex-1">
                                <h4 className="font-semibold text-slate-900">{getServiceName(service.service)}</h4>
                                <p className="text-sm text-slate-600 mt-1">
                                  {service.calls.toLocaleString()} calls • {service.tokens.toLocaleString()} tokens
                                </p>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-amber-700">
                                  ${service.total_cost.toFixed(4)}
                                </div>
                                <div className="text-xs text-slate-500">
                                  ${(service.total_cost / service.calls).toFixed(4)}/call
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              <Card className="border-2 border-dashed border-slate-300">
                <CardContent className="py-16 text-center">
                  <div className="text-6xl mb-4">📊</div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No API Usage Data</h3>
                  <p className="text-slate-600">
                    API costs will appear here once services start logging their usage.
                  </p>
                  <p className="text-sm text-slate-500 mt-2">
                    Try selecting a different time period or check back later.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* API Provider Breakdown */}
            {apiCostsData.by_provider && apiCostsData.by_provider.length > 0 && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Costs by API Provider</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {apiCostsData.by_provider.map((provider) => (
                    <Card key={provider.provider} className="hover:shadow-lg transition-shadow">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold text-slate-900 text-lg mb-2">{provider.provider}</h4>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-600">{provider.calls.toLocaleString()} calls</span>
                          <span className="text-2xl font-bold text-slate-900">${provider.total_cost.toFixed(4)}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Most Expensive Operations */}
            {apiCostsData.top_operations && apiCostsData.top_operations.length > 0 && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Most Expensive Operations</h2>
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      {apiCostsData.top_operations.map((op, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                          <div className="flex items-center gap-4">
                            <div className="w-8 h-8 rounded-full bg-red-100 text-red-700 flex items-center justify-center font-bold">
                              {index + 1}
                            </div>
                            <div>
                              <h4 className="font-semibold text-slate-900">
                                {getServiceName(op.service)} → {op.operation}
                              </h4>
                              <p className="text-sm text-slate-600">
                                {op.calls.toLocaleString()} calls @ ${op.avg_cost_per_call.toFixed(4)}/call avg
                              </p>
                            </div>
                          </div>
                          <div className="text-2xl font-bold text-red-600">
                            ${op.total_cost.toFixed(4)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        ) : (
          <Card className="border-2 border-dashed border-slate-300">
            <CardContent className="py-16 text-center">
              <div className="text-6xl mb-4">💰</div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">API Cost Analytics</h3>
              <p className="text-slate-600">
                Select a time period to view API cost data
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default APICostsPage;
